import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { criarAlimento } from "../db/queries";
import { salvarFoto } from "../lib/foto";
import { urlIlustracao } from "../lib/catalogoImagens";
import { SeletorCatalogo } from "../ui/SeletorCatalogo";
import { GRUPOS_ORDEM, GRUPO_LABEL, type GrupoAlimentar, type StatusAlimento } from "../types";

/**
 * P0.1 — "definir alimento": cadastro rico, feito 1×. Pode ser lento (≠ do
 * registro de oferta). Foto via câmera/galeria (ref local), 5 dimensões sensoriais.
 */
export function NovoAlimento() {
  const nav = useNavigate();
  const [nome, setNome] = useState("");
  const [marca, setMarca] = useState("");
  const [categoria, setCategoria] = useState("");
  const [status, setStatus] = useState<StatusAlimento>("nao_oferecido");
  const [grupo, setGrupo] = useState<GrupoAlimentar | "">("");
  const [obs, setObs] = useState("");
  const [sensorial, setSensorial] = useState({
    textura: "",
    temperatura: "",
    cor: "",
    forma_apresentacao: "",
    cheiro: "",
  });
  const [fotoId, setFotoId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [mostrarCatalogo, setMostrarCatalogo] = useState(false);
  const [salvando, setSalvando] = useState(false);

  async function escolherFoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const id = await salvarFoto(file);
    setFotoId(id);
    setPreviewUrl(URL.createObjectURL(file));
  }

  function escolherDoCatalogo(ref: string) {
    setFotoId(ref);
    setPreviewUrl(urlIlustracao(ref));
    setMostrarCatalogo(false);
  }

  async function salvar() {
    if (!nome.trim() || salvando) return;
    setSalvando(true);
    const limpo = (s: string) => (s.trim() ? s.trim() : null);
    const id = await criarAlimento({
      nome,
      marca_produto: limpo(marca),
      categoria: limpo(categoria),
      status,
      grupo: grupo || null,
      observacoes: limpo(obs),
      fotos: fotoId ? [fotoId] : [],
      sensorial: {
        textura: limpo(sensorial.textura),
        temperatura: limpo(sensorial.temperatura),
        cor: limpo(sensorial.cor),
        forma_apresentacao: limpo(sensorial.forma_apresentacao),
        cheiro: limpo(sensorial.cheiro),
      },
    });
    nav(`/alimento/${id}`, { replace: true });
  }

  const sens = (k: keyof typeof sensorial, rotulo: string, ph: string) => (
    <div className="campo">
      <label>{rotulo}</label>
      <input
        value={sensorial[k]}
        placeholder={ph}
        onChange={(e) => setSensorial((s) => ({ ...s, [k]: e.target.value }))}
      />
    </div>
  );

  return (
    <main className="tela">
      <button className="btn btn-fantasma" onClick={() => nav(-1)}>
        ← voltar
      </button>
      <h1 className="tela-titulo">novo alimento</h1>

      <div className="card">
        <div className="campo">
          <label>nome</label>
          <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="ex.: morango" autoFocus />
        </div>

        <div className="campo">
          <label>foto</label>
          {previewUrl && (
            <img
              src={previewUrl}
              alt=""
              style={{ width: "100%", maxHeight: 200, objectFit: "cover", borderRadius: "var(--r-md)", marginBottom: "var(--s2)" }}
            />
          )}
          <input type="file" accept="image/*" capture="environment" onChange={escolherFoto} />
          <button
            type="button"
            className="btn btn-fantasma btn-bloco"
            style={{ marginTop: "var(--s2)" }}
            onClick={() => setMostrarCatalogo((v) => !v)}
          >
            🖼️ {mostrarCatalogo ? "fechar catálogo" : "escolher do catálogo"}
          </button>
          {mostrarCatalogo && (
            <SeletorCatalogo selecionada={fotoId} onPick={escolherDoCatalogo} />
          )}
        </div>

        <div className="linha-campos">
          <div className="campo">
            <label>marca / produto</label>
            <input value={marca} onChange={(e) => setMarca(e.target.value)} placeholder="opcional" />
          </div>
          <div className="campo">
            <label>categoria</label>
            <input value={categoria} onChange={(e) => setCategoria(e.target.value)} placeholder="ex.: fruta" />
          </div>
        </div>

        <div className="linha-campos">
          <div className="campo">
            <label>status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value as StatusAlimento)}>
              <option value="nao_oferecido">não oferecido</option>
              <option value="em_teste">em teste</option>
              <option value="seguro">seguro</option>
              <option value="recusado">recusado</option>
            </select>
          </div>
          <div className="campo">
            <label>grupo alimentar</label>
            <select value={grupo} onChange={(e) => setGrupo(e.target.value as GrupoAlimentar | "")}>
              <option value="">—</option>
              {GRUPOS_ORDEM.map((g) => (
                <option key={g} value={g}>
                  {GRUPO_LABEL[g]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <h2 className="secao-titulo" style={{ marginTop: "var(--s5)" }}>
        propriedades sensoriais
      </h2>
      <div className="card">
        <div className="linha-campos">
          {sens("textura", "textura", "macia, crocante…")}
          {sens("temperatura", "temperatura", "gelada, morna…")}
          {sens("cor", "cor", "vermelha…")}
          {sens("forma_apresentacao", "forma", "em cubos…")}
        </div>
        {sens("cheiro", "cheiro", "doce, neutro…")}
      </div>

      <div className="card" style={{ marginTop: "var(--s4)" }}>
        <div className="campo" style={{ margin: 0 }}>
          <label>observações</label>
          <textarea value={obs} onChange={(e) => setObs(e.target.value)} placeholder="opcional" />
        </div>
      </div>

      <button
        className="btn btn-primario btn-bloco btn-grande"
        style={{ marginTop: "var(--s5)" }}
        disabled={!nome.trim() || salvando}
        onClick={salvar}
      >
        salvar alimento
      </button>
    </main>
  );
}
